/**
 * useTaxonomies Hook
 *
 * Fetches property taxonomies (types, states, areas, cities, amenities)
 * from Firestore: the flat propertyTypes/amenities collections managed in
 * Admin → Content, plus the nested locations tree from the seed scripts.
 * Falls back to the hardcoded defaults per-section when a collection is
 * empty, so the listing wizard never renders blank.
 */

import { useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../../../services/firebase';
import { logError } from '../../../../utils/errors';

export interface TaxonomyItem {
  slug: string;
  name: string;
  nameAr?: string;
  parentSlug?: string;
  order?: number;
}

export interface AmenityItem {
  id: string;
  name: string;
  nameAr?: string;
  category: 'essentials' | 'features' | 'kitchen' | 'safety' | 'outdoor';
  icon?: string;
  isPopular?: boolean;
}

export interface Taxonomies {
  propertyTypes: TaxonomyItem[];
  states: TaxonomyItem[];
  areas: TaxonomyItem[];
  cities: TaxonomyItem[];
  amenities: AmenityItem[];
}

// Fallback taxonomy data (used per-section when Firestore is empty)
const fallbackTaxonomies: Taxonomies = {
  propertyTypes: [
    { slug: 'chalet', name: 'Chalet', nameAr: 'شاليه' },
    { slug: 'apartment', name: 'Apartment', nameAr: 'شقة' },
    { slug: 'villa', name: 'Villa', nameAr: 'فيلا' },
    { slug: 'house', name: 'House', nameAr: 'منزل' },
    { slug: 'cabin', name: 'Cabin', nameAr: 'كوخ' },
    { slug: 'studio', name: 'Studio', nameAr: 'استوديو' },
    { slug: 'guesthouse', name: 'Guesthouse', nameAr: 'بيت ضيافة' },
    { slug: 'resort', name: 'Resort', nameAr: 'منتجع' },
  ],
  states: [
    { slug: 'mount-lebanon', name: 'Mount Lebanon', nameAr: 'جبل لبنان' },
    { slug: 'north-lebanon', name: 'North Lebanon', nameAr: 'شمال لبنان' },
    { slug: 'south-lebanon', name: 'South Lebanon', nameAr: 'جنوب لبنان' },
    { slug: 'beirut', name: 'Beirut', nameAr: 'بيروت' },
    { slug: 'bekaa', name: 'Bekaa', nameAr: 'البقاع' },
    { slug: 'nabatieh', name: 'Nabatieh', nameAr: 'النبطية' },
    { slug: 'baalbek-hermel', name: 'Baalbek-Hermel', nameAr: 'بعلبك الهرمل' },
    { slug: 'akkar', name: 'Akkar', nameAr: 'عكار' },
  ],
  areas: [
    // Mount Lebanon
    { slug: 'keserwan', name: 'Keserwan', parentSlug: 'mount-lebanon' },
    { slug: 'metn', name: 'Metn', parentSlug: 'mount-lebanon' },
    { slug: 'baabda', name: 'Baabda', parentSlug: 'mount-lebanon' },
    { slug: 'chouf', name: 'Chouf', parentSlug: 'mount-lebanon' },
    { slug: 'jbeil', name: 'Jbeil', parentSlug: 'mount-lebanon' },
    { slug: 'aley', name: 'Aley', parentSlug: 'mount-lebanon' },
    // North Lebanon
    { slug: 'zgharta', name: 'Zgharta', parentSlug: 'north-lebanon' },
    { slug: 'koura', name: 'Koura', parentSlug: 'north-lebanon' },
    { slug: 'batroun', name: 'Batroun', parentSlug: 'north-lebanon' },
    { slug: 'bsharri', name: 'Bsharri', parentSlug: 'north-lebanon' },
    { slug: 'tripoli', name: 'Tripoli', parentSlug: 'north-lebanon' },
    // Bekaa
    { slug: 'zahle', name: 'Zahle', parentSlug: 'bekaa' },
    { slug: 'west-bekaa', name: 'West Bekaa', parentSlug: 'bekaa' },
    { slug: 'rashaya', name: 'Rashaya', parentSlug: 'bekaa' },
    // South Lebanon
    { slug: 'jezzine', name: 'Jezzine', parentSlug: 'south-lebanon' },
    { slug: 'saida', name: 'Saida', parentSlug: 'south-lebanon' },
    { slug: 'tyre', name: 'Tyre', parentSlug: 'south-lebanon' },
  ],
  cities: [
    // Keserwan
    { slug: 'faraya', name: 'Faraya', parentSlug: 'keserwan' },
    { slug: 'faqra', name: 'Faqra', parentSlug: 'keserwan' },
    { slug: 'jounieh', name: 'Jounieh', parentSlug: 'keserwan' },
    { slug: 'zouk-mosbeh', name: 'Zouk Mosbeh', parentSlug: 'keserwan' },
    { slug: 'ghosta', name: 'Ghosta', parentSlug: 'keserwan' },
    { slug: 'kfour', name: 'Kfour', parentSlug: 'keserwan' },
    // Metn
    { slug: 'broummana', name: 'Broummana', parentSlug: 'metn' },
    { slug: 'beit-mery', name: 'Beit Mery', parentSlug: 'metn' },
    { slug: 'bikfaya', name: 'Bikfaya', parentSlug: 'metn' },
    // Jbeil
    { slug: 'byblos', name: 'Byblos', parentSlug: 'jbeil' },
    { slug: 'laqlouq', name: 'Laqlouq', parentSlug: 'jbeil' },
    { slug: 'aannaya', name: 'Aannaya', parentSlug: 'jbeil' },
    // Batroun
    { slug: 'batroun-city', name: 'Batroun', parentSlug: 'batroun' },
    { slug: 'tannourine', name: 'Tannourine', parentSlug: 'batroun' },
    { slug: 'douma', name: 'Douma', parentSlug: 'batroun' },
    // Bsharri
    { slug: 'cedars', name: 'The Cedars', parentSlug: 'bsharri' },
    { slug: 'bsharri-town', name: 'Bsharri', parentSlug: 'bsharri' },
    { slug: 'hasroun', name: 'Hasroun', parentSlug: 'bsharri' },
    // Jezzine
    { slug: 'jezzine-town', name: 'Jezzine', parentSlug: 'jezzine' },
    { slug: 'bkassine', name: 'Bkassine', parentSlug: 'jezzine' },
    // Zahle
    { slug: 'zahle-city', name: 'Zahle', parentSlug: 'zahle' },
    { slug: 'ksara', name: 'Ksara', parentSlug: 'zahle' },
  ],
  amenities: [
    // Essentials
    { id: 'wifi', name: 'WiFi', category: 'essentials', isPopular: true },
    { id: 'air-conditioning', name: 'Air conditioning', category: 'essentials', isPopular: true },
    { id: 'heating', name: 'Heating', category: 'essentials', isPopular: true },
    { id: 'tv', name: 'TV', category: 'essentials' },
    { id: 'hot-water', name: 'Hot water', category: 'essentials' },
    { id: 'towels', name: 'Towels', category: 'essentials' },
    { id: 'bed-linens', name: 'Bed linens', category: 'essentials' },
    { id: 'hangers', name: 'Hangers', category: 'essentials' },
    { id: 'iron', name: 'Iron', category: 'essentials' },
    { id: 'hair-dryer', name: 'Hair dryer', category: 'essentials' },
    // Features
    { id: 'pool', name: 'Swimming pool', category: 'features', isPopular: true },
    { id: 'hot-tub', name: 'Hot tub', category: 'features' },
    { id: 'gym', name: 'Gym', category: 'features' },
    { id: 'parking', name: 'Free parking', category: 'features', isPopular: true },
    { id: 'washer', name: 'Washer', category: 'features' },
    { id: 'dryer', name: 'Dryer', category: 'features' },
    { id: 'workspace', name: 'Dedicated workspace', category: 'features' },
    { id: 'elevator', name: 'Elevator', category: 'features' },
    { id: 'fireplace', name: 'Fireplace', category: 'features' },
    // Kitchen
    { id: 'kitchen', name: 'Kitchen', category: 'kitchen', isPopular: true },
    { id: 'refrigerator', name: 'Refrigerator', category: 'kitchen' },
    { id: 'microwave', name: 'Microwave', category: 'kitchen' },
    { id: 'oven', name: 'Oven', category: 'kitchen' },
    { id: 'stove', name: 'Stove', category: 'kitchen' },
    { id: 'dishwasher', name: 'Dishwasher', category: 'kitchen' },
    { id: 'coffee-maker', name: 'Coffee maker', category: 'kitchen' },
    { id: 'cooking-basics', name: 'Cooking basics', category: 'kitchen' },
    // Safety
    { id: 'smoke-alarm', name: 'Smoke alarm', category: 'safety' },
    { id: 'carbon-monoxide', name: 'Carbon monoxide alarm', category: 'safety' },
    { id: 'fire-extinguisher', name: 'Fire extinguisher', category: 'safety' },
    { id: 'first-aid', name: 'First aid kit', category: 'safety' },
    { id: 'security-cameras', name: 'Security cameras', category: 'safety' },
    // Outdoor
    { id: 'balcony', name: 'Balcony', category: 'outdoor' },
    { id: 'patio', name: 'Patio', category: 'outdoor' },
    { id: 'garden', name: 'Garden', category: 'outdoor' },
    { id: 'bbq-grill', name: 'BBQ grill', category: 'outdoor' },
    { id: 'outdoor-dining', name: 'Outdoor dining area', category: 'outdoor' },
    { id: 'mountain-view', name: 'Mountain view', category: 'outdoor', isPopular: true },
    { id: 'sea-view', name: 'Sea view', category: 'outdoor', isPopular: true },
  ],
};

const AMENITY_CATEGORIES = new Set([
  'essentials',
  'features',
  'kitchen',
  'safety',
  'outdoor',
]);

async function fetchTaxonomies(): Promise<Taxonomies> {
  const [propertyTypes, amenities, geography] = await Promise.all([
    fetchPropertyTypes(),
    fetchAmenities(),
    fetchGeography(),
  ]);

  return {
    propertyTypes:
      propertyTypes.length > 0 ? propertyTypes : fallbackTaxonomies.propertyTypes,
    amenities: amenities.length > 0 ? amenities : fallbackTaxonomies.amenities,
    states: geography.states.length > 0 ? geography.states : fallbackTaxonomies.states,
    areas: geography.areas.length > 0 ? geography.areas : fallbackTaxonomies.areas,
    cities: geography.cities.length > 0 ? geography.cities : fallbackTaxonomies.cities,
  };
}

async function fetchPropertyTypes(): Promise<TaxonomyItem[]> {
  try {
    const snap = await getDocs(collection(db, 'propertyTypes'));
    return snap.docs
      .map((d) => {
        const data = d.data();
        return {
          slug: (data.slug as string) || d.id,
          name: (data.name as string) || d.id,
          nameAr: data.nameAr as string | undefined,
          order: data.order as number | undefined,
        };
      })
      .sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
  } catch {
    return [];
  }
}

async function fetchAmenities(): Promise<AmenityItem[]> {
  try {
    const snap = await getDocs(collection(db, 'amenities'));
    return snap.docs.map((d) => {
      const data = d.data();
      const rawCategory = (data.category as string) || '';
      return {
        id: (data.slug as string) || d.id,
        name: (data.name as string) || d.id,
        nameAr: data.nameAr as string | undefined,
        category: (AMENITY_CATEGORIES.has(rawCategory)
          ? rawCategory
          : 'features') as AmenityItem['category'],
        icon: data.icon as string | undefined,
        isPopular: data.isPopular === true,
      };
    });
  } catch {
    return [];
  }
}

async function fetchGeography(): Promise<{
  states: TaxonomyItem[];
  areas: TaxonomyItem[];
  cities: TaxonomyItem[];
}> {
  try {
    const statesSnap = await getDocs(
      collection(db, 'locations/countries/list/lebanon/states')
    );
    const states: TaxonomyItem[] = [];
    const areas: TaxonomyItem[] = [];
    const cities: TaxonomyItem[] = [];

    await Promise.all(
      statesSnap.docs.map(async (stateDoc) => {
        const stateData = stateDoc.data();
        const stateSlug = (stateData.slug as string) || stateDoc.id;
        states.push({
          slug: stateSlug,
          name: (stateData.name as string) || stateDoc.id,
          nameAr: stateData.nameAr as string | undefined,
        });

        const areasSnap = await getDocs(
          collection(db, `locations/countries/list/lebanon/states/${stateDoc.id}/areas`)
        );
        areasSnap.forEach((areaDoc) => {
          const areaData = areaDoc.data();
          const areaSlug = (areaData.slug as string) || areaDoc.id;
          areas.push({
            slug: areaSlug,
            name: (areaData.name as string) || areaDoc.id,
            nameAr: areaData.nameAr as string | undefined,
            parentSlug: stateSlug,
          });
          const embeddedCities =
            (areaData.cities as Array<{ slug?: string; name?: string; nameAr?: string }>) || [];
          for (const city of embeddedCities) {
            if (!city?.name) continue;
            cities.push({
              slug: city.slug || city.name.toLowerCase().replace(/\s+/g, '-'),
              name: city.name,
              nameAr: city.nameAr,
              parentSlug: areaSlug,
            });
          }
        });
      })
    );

    states.sort((a, b) => a.name.localeCompare(b.name));
    areas.sort((a, b) => a.name.localeCompare(b.name));
    cities.sort((a, b) => a.name.localeCompare(b.name));
    return { states, areas, cities };
  } catch {
    return { states: [], areas: [], cities: [] };
  }
}

export const useTaxonomies = () => {
  const [data, setData] = useState<Taxonomies | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;

    fetchTaxonomies()
      .then((taxonomies) => {
        if (!cancelled) setData(taxonomies);
      })
      .catch((err) => {
        logError(err, { source: 'useTaxonomies' });
        if (!cancelled) setData(fallbackTaxonomies);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return { data, isLoading, error };
};

export default useTaxonomies;
